import React from "react";
import { Link as RouterLink } from "react-router-dom";
import SectionHead from "../ui/SectionHead";
import Reveal from "../ui/Reveal";
import { SERVICES } from "../../data/site";
import { ArrowRight } from "../ui/Icons";
import "./Services.css";

/* ---------- illustrations ---------- */
function ArtPlatform() {
  const tiles = [
    { b: [6, 10, 20, 12] },
    { b: [10, 8, 18, 14], live: true },
    { b: [8, 14, 22, 10] },
    { b: [12, 6, 16, 16] },
    { b: [5, 12, 26, 10] },
    { b: [14, 10, 14, 12] },
  ];
  return (
    <svg className="art" viewBox="0 0 300 160" aria-hidden="true">
      <defs>
        <linearGradient id="tileG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a2129" />
          <stop offset="1" stopColor="#0d1116" />
        </linearGradient>
      </defs>
      {tiles.map((t, i) => {
        const x = 16 + (i % 3) * 92;
        const y = 16 + Math.floor(i / 3) * 66;
        return (
          <g key={i} transform={`translate(${x} ${y})`}>
            <rect width="84" height="56" rx="6" fill="url(#tileG)" stroke="rgba(255,255,255,.12)" />
            <rect
              x={t.b[0] + 18}
              y={t.b[1]}
              width={t.b[2]}
              height={t.b[3]}
              fill="none"
              stroke={t.live ? "var(--accent)" : "rgba(93,242,164,.55)"}
              strokeWidth="1.2"
              className={t.live ? "art__box art__box--live" : "art__box"}
            />
            <rect x="6" y="42" width="26" height="6" rx="2" fill="rgba(255,255,255,.12)" />
            <text x="6" y="12" fontSize="6" fill="rgba(255,255,255,.55)" fontFamily="JetBrains Mono, monospace">
              CAM-0{i + 1}
            </text>
            {t.live && <circle cx="76" cy="10" r="2.5" fill="var(--danger)" className="art__blink" />}
          </g>
        );
      })}
    </svg>
  );
}

function ArtEquip() {
  return (
    <svg className="art" viewBox="0 0 300 160" aria-hidden="true">
      <defs>
        <linearGradient id="chipG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgba(93,242,164,.35)" />
          <stop offset="1" stopColor="rgba(93,242,164,.08)" />
        </linearGradient>
      </defs>
      {/* existing camera */}
      <g transform="translate(28 52)">
        <rect width="70" height="44" rx="8" fill="#141a20" stroke="rgba(255,255,255,.16)" />
        <circle cx="26" cy="22" r="11" fill="#0b0e12" stroke="rgba(255,255,255,.25)" />
        <circle cx="26" cy="22" r="5" fill="rgba(79,211,255,.7)" />
        <rect x="46" y="14" width="16" height="4" rx="2" fill="rgba(255,255,255,.2)" />
        <rect x="46" y="24" width="10" height="4" rx="2" fill="rgba(255,255,255,.2)" />
        <text x="0" y="60" fontSize="7" fill="rgba(255,255,255,.5)" fontFamily="JetBrains Mono, monospace">
          EXISTING CCTV
        </text>
      </g>
      {/* link */}
      <path id="eqPath" d="M100 74 C 130 74, 140 74, 172 74" stroke="rgba(93,242,164,.5)" strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <circle r="3" fill="var(--accent)">
        <animateMotion dur="1.8s" repeatCount="indefinite">
          <mpath href="#eqPath" />
        </animateMotion>
      </circle>
      {/* AI module */}
      <g transform="translate(172 42)">
        <rect width="100" height="64" rx="10" fill="url(#chipG)" stroke="rgba(93,242,164,.6)" />
        <rect x="14" y="14" width="72" height="36" rx="6" fill="#0b0e12" stroke="rgba(93,242,164,.4)" />
        <text x="50" y="36" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--accent)" fontFamily="Space Grotesk, sans-serif">
          AI MODULE
        </text>
        {[0, 1, 2, 3, 4].map((i) => (
          <React.Fragment key={i}>
            <rect x={16 + i * 16} y="-6" width="6" height="6" fill="rgba(93,242,164,.6)" />
            <rect x={16 + i * 16} y="64" width="6" height="6" fill="rgba(93,242,164,.6)" />
          </React.Fragment>
        ))}
        <text x="0" y="86" fontSize="7" fill="rgba(255,255,255,.5)" fontFamily="JetBrains Mono, monospace">
          DETECT · TRACK · ALERT
        </text>
      </g>
    </svg>
  );
}

function ArtBuild() {
  const pts = [
    [30, 120],
    [110, 60],
    [190, 96],
    [270, 40],
  ];
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  return (
    <svg className="art art--blueprint" viewBox="0 0 300 160" aria-hidden="true">
      <defs>
        <pattern id="bp" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="rgba(93,242,164,.12)" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect width="300" height="160" fill="url(#bp)" />
      <path d={d} fill="none" stroke="rgba(93,242,164,.7)" strokeWidth="1.5" className="art__path" />
      {pts.map((p, i) => (
        <g key={i} transform={`translate(${p[0]} ${p[1]})`}>
          <circle r="9" fill="#0b0e12" stroke="var(--accent)" strokeWidth="1.5" />
          <text y="3" textAnchor="middle" fontSize="7" fill="var(--accent)" fontFamily="JetBrains Mono, monospace">
            0{i + 1}
          </text>
          <text y="24" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,.55)" fontFamily="JetBrains Mono, monospace">
            {["DISCOVER", "PLAN", "BUILD", "DELIVER"][i]}
          </text>
        </g>
      ))}
    </svg>
  );
}

const ART = {
  "vision-platform": ArtPlatform,
  "equip-intelligence": ArtEquip,
  "build-solutions": ArtBuild,
};

export default function Services() {
  return (
    <section id="services" className="section">
      <div className="container">
        <SectionHead
          eyebrow="What we do"
          title="Three ways to bring vision AI into your operation."
          lead="Whether you start from scratch, already run cameras, or need something no off-the-shelf model covers, there is a path that fits."
        />
        <div className="svc">
          {SERVICES.map((s, i) => {
            const Art = ART[s.id];
            return (
              <Reveal
                key={s.id}
                className="svc__card card card--hover"
                delay={i * 0.1}
              >
                <div className="svc__art">
                  <Art />
                </div>
                <span className="svc__num mono">{s.num}</span>
                <h3 className="h3">{s.title}</h3>
                <p className="svc__desc">{s.description}</p>
                <div className="chips">
                  {s.chips.map((c) => (
                    <span key={c} className="chip">
                      {c}
                    </span>
                  ))}
                </div>
                <RouterLink to={s.path} className="link-arrow svc__link">
                  Explore {s.title} <ArrowRight className="i" />
                </RouterLink>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
